import { useState } from 'react';
import { useCart } from '../../contexts/CartContext';
import { createClient } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CheckoutModal({ isOpen, onClose }: CheckoutModalProps) {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'flutterwave' | 'whatsapp'>('flutterwave');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (paymentMethod === 'flutterwave' && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleWhatsAppOrder = () => {
    if (!validateForm()) return;

    const orderDetails = cartItems.map(item => 
      `*${item.name}* x${item.quantity} - ₦${(item.priceNumeric * item.quantity).toLocaleString()}`
    ).join('%0A');

    const message = `Hello! I would like to place an order:%0A%0A` +
      `📦 *ORDER DETAILS*%0A${orderDetails}%0A%0A` +
      `💰 *Total: ₦${getCartTotal().toLocaleString()}*%0A%0A` +
      `👤 *CUSTOMER INFORMATION*%0A` +
      `Name: ${formData.name}%0A` +
      `Phone: ${formData.phone}%0A` +
      (formData.email ? `Email: ${formData.email}%0A` : '') +
      `%0APlease confirm my order. Thank you!`;

    window.open(`https://wa.me/2348154358746?text=${message}`, '_blank');
    clearCart();
    onClose();
    navigate('/thank-you');
  };

  const handleFlutterwavePayment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/flutterwave-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            cartItems: cartItems.map(item => ({
              name: item.name,
              quantity: item.quantity,
              priceNumeric: item.priceNumeric,
            })),
            customerEmail: formData.email || `${formData.phone}@placeholder.com`,
            customerName: formData.name,
            customerPhone: formData.phone,
            origin: window.location.origin,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment initialization failed');
      }

      const data = await response.json();

      if (data.success && data.payment_link) {
        await supabase.from('orders').insert({
          tx_ref: data.tx_ref,
          customer_name: formData.name,
          customer_email: formData.email || `${formData.phone}@placeholder.com`,
          customer_phone: formData.phone,
          total_amount: getCartTotal(),
          cart_items: cartItems,
          status: 'pending',
        });

        window.location.href = data.payment_link;
      } else {
        throw new Error('Payment link not received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(error instanceof Error ? error.message : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (paymentMethod === 'whatsapp') {
      handleWhatsAppOrder();
    } else {
      handleFlutterwavePayment(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Checkout</h2>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors hover:bg-gray-100 rounded-lg cursor-pointer">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">Order Summary</h3>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              {cartItems.map((item) => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span className="text-gray-600 flex-1 pr-2">{item.name} × {item.quantity}</span>
                  <span className="font-medium text-gray-900 whitespace-nowrap">₦{(item.priceNumeric * item.quantity).toLocaleString()}</span>
                </div>
              ))}
              <div className="pt-2 border-t border-gray-200 flex justify-between">
                <span className="font-semibold text-gray-900 text-sm">Total</span>
                <span className="font-bold text-teal-600 text-lg">₦{getCartTotal().toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">Payment Method</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setPaymentMethod('flutterwave')}
                className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  paymentMethod === 'flutterwave'
                    ? 'border-teal-600 bg-teal-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <i className="ri-bank-card-line text-2xl mb-2 block text-teal-600"></i>
                <span className="text-sm font-medium text-gray-900">Card Payment</span>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('whatsapp')}
                className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  paymentMethod === 'whatsapp'
                    ? 'border-green-600 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <i className="ri-whatsapp-line text-2xl mb-2 block text-green-600"></i>
                <span className="text-sm font-medium text-gray-900">WhatsApp Order</span>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full px-4 py-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm`}
                placeholder="Enter your full name"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address {paymentMethod === 'whatsapp' && <span className="text-gray-500">(Optional)</span>}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm`}
                placeholder="your@email.com"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className={`w-full px-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm`}
                placeholder="+234 XXX XXX XXXX"
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            {paymentMethod === 'flutterwave' ? (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 flex gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-shield-check-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-teal-900 font-medium mb-0.5">Secure Payment with Flutterwave</p>
                  <p className="text-xs text-teal-700">Pay with cards, bank transfer, or USSD. 100% secure.</p>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-whatsapp-line text-green-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-green-900 font-medium mb-0.5">Order via WhatsApp</p>
                  <p className="text-xs text-green-700">Send your order directly to us on WhatsApp for confirmation.</p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3.5 px-6 rounded-lg font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2 shadow-lg text-sm cursor-pointer ${
                paymentMethod === 'whatsapp'
                  ? 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
                  : 'bg-teal-600 text-white hover:bg-teal-700 active:bg-teal-800'
              }`}
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line text-xl animate-spin"></i>
                  Processing...
                </>
              ) : paymentMethod === 'whatsapp' ? (
                <>
                  <i className="ri-whatsapp-line text-xl"></i>
                  Send Order via WhatsApp
                </>
              ) : (
                <>
                  <i className="ri-secure-payment-line text-xl"></i>
                  Proceed to Payment
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
